import { forwardRef, lazy, useState } from "react";
import { Modal, Paper, Typography } from "@mui/material";
import PropTypes from "prop-types";

import LazyLoader from "src/components/layout/Progress/LazyLoader";

import StepButtons from "src/components/buttons/StepButtons/StepButtons";
import { CloseIcon } from "src/components/icons/ActionIcons";
import CloseDialogIcon from "src/components/icons/purposed/CloseDialogIcon";
import HelperIcon from "src/components/icons/purposed/HelperIcon/HelperIcon";
import ProgressBar from "src/components/layout/Progress/ProgressBar";

const MuokkaustilaButton = lazy(() =>
  import("src/components/buttons/purposed/MuokkaustilaButton/MuokkaustilaButton")
);

const styles = {
  modal: {
    overflow: "scroll",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    marginTop: "50px",
  },
  titleDiv1: {
    minHeight: "57px",
    paddingLeft: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  titleDiv2: { display: "flex", alignItems: "center" },
  closeIconDiv: { position: "relative", top: "0px" },
};

export default function StepModal({
  hideBackdrop = false,
  editMode = false,
  hideButtons = false,
  rightButtonText = "TALLENNA",
  dataIsChanged = false, // if data is changed, show warning when closing
  closeButtonText = "SULJE",
  onCancelKlik = () => {},
  onClose = () => {},
  onSaveKlik = () => {},
  title = "",
  showMuokkausTilaButton = false,
  fullHeight = false,
  fullWidth = false,
  rowId = 0, // the rowId of the row that is being edited, shown in the title as small caption, for example the tuoteId of tuote modal etc.
  isLoading = false,
  helpId,
  rightButtonDisabled,
  ...props
}) {
  const [isLoadingState, setIsLoadingState] = useState(false);
  const currentIsLoading = isLoading || isLoadingState;

  const open = props.open || props.isOpen || false;

  const DraggablePaper = forwardRef((props, ref) => (
    <div ref={ref}>
      <Paper elevation={3} style={localStyles.paper} aria-labelledby="draggable-dialog-title">
        {props.children}
      </Paper>
    </div>
  ));
  DraggablePaper.displayName = "DraggablePaper";

  const handleCancelKlik = async (askToSave) => {
    setIsLoadingState(true);
    onCancelKlik && (await onCancelKlik(askToSave));
    onClose && (await onClose());
    setIsLoadingState(false);
  };

  const handleSaveClick = async () => {
    setIsLoadingState(true);
    onSaveKlik && (await onSaveKlik());
    onClose && (await onClose());
    setIsLoadingState(false);
  };

  const localStyles = {
    paper: {
      margin: "auto",
      flexDirection: "column",
      padding: "5px",
      width: "800px",
      maxWidth: "99vw",
      minHeight: fullHeight ? "90vh" : "default",
      minWidth: fullWidth ? "99vw" : "default",
    },
  };

  return (
    <Modal
      hideBackdrop={hideBackdrop}
      open={open}
      slotProps={{ timeout: 1500 }}
      disableScrollLock={true}
      style={styles.modal}>
      <div>
        <DraggablePaper>
          <div className="harmaaTausta" style={styles.titleDiv1}>
            <div className="spaceBetween" id="draggable-dialog-title">
              <div className="center" style={{ gap: 12 }}>
                <Typography color="primary" variant="h5" align="center" className="fixPrint">
                  {title}
                  <span className="idNum noPrint onlyWide">{rowId > 0 && `#${rowId}`}</span>
                </Typography>
                {helpId && <HelperIcon helpId={helpId} />}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {showMuokkausTilaButton && (
                  <LazyLoader>
                    <MuokkaustilaButton hideHelp />
                  </LazyLoader>
                )}
                <CloseDialogIcon
                  onClick={() => handleCancelKlik(true)}
                  dataIsChanged={dataIsChanged}
                />
              </div>
            </div>
          </div>
          <ProgressBar show={currentIsLoading} />
          <div className="harmaaTausta">
            <Paper elevation={2} className="stepModal">
              {props.children}
              {!hideButtons &&
                (editMode ? (
                  <StepButtons
                    leftButtonText="PERUUTA"
                    onCancelKlik={() => handleCancelKlik(false)}
                    rightButtonText={rightButtonText}
                    onSaveKlik={handleSaveClick}
                    rightButtonDisabled={rightButtonDisabled || false}
                    closeText={closeButtonText}
                  />
                ) : (
                  <StepButtons
                    closeText={closeButtonText}
                    closeIcon={<CloseIcon />}
                    onCloseKlik={() => handleCancelKlik(false)}
                  />
                ))}
            </Paper>
          </div>
        </DraggablePaper>
      </div>
    </Modal>
  );
}

StepModal.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  isOpen: PropTypes.bool,
  hideBackdrop: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  helpId: PropTypes.string,
  editMode: PropTypes.bool,
  onCancelKlik: PropTypes.func,
  onClose: PropTypes.func,
  onSaveKlik: PropTypes.func,
  rightButtonDisabled: PropTypes.bool,
  hideButtons: PropTypes.bool,
  rightButtonText: PropTypes.string,
  dataIsChanged: PropTypes.bool,
  closeButtonText: PropTypes.string,
  showMuokkausTilaButton: PropTypes.bool,
  fullHeight: PropTypes.bool,
  fullWidth: PropTypes.bool,
  rowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLoading: PropTypes.bool,
};
